import React, { useState, useEffect, useCallback } from 'react';
import { Divider, Table, Button, message, Tag, Modal } from 'antd';
import { axios } from '../../../../../../axiosConfig';
import { formatDate } from '../../../../../helpers/date';
import MergeContactModal from '../../components/MergeContactModal';

const MergedContactsSection = ({ contact, onContactUpdated }) => {
  const [mergedContacts, setMergedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const loadMergedContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${contact.id}/merged`);
      setMergedContacts(response.data.data || []);
    } catch (error) {
      console.error('Error cargando contactos fusionados:', error);
      message.error('Error al cargar contactos fusionados');
    } finally {
      setLoading(false);
    }
  }, [contact]);

  const handleMergeSuccess = () => {
    loadMergedContacts(); // Recargar la lista de contactos fusionados
    setShowMergeModal(false);
    // Notificar al componente padre que el contacto ha sido actualizado
    if (onContactUpdated) {
      onContactUpdated();
    }
  };

  useEffect(() => {
    if (contact?.id) {
      loadMergedContacts();
    }
  }, [contact, loadMergedContacts]);

  const handleUnfuse = (mergeRecord) => {
    Modal.confirm({
      title: '¿Deshacer fusión?',
      content: `¿Estás seguro de que quieres deshacer la fusión con ${mergeRecord.mergedContact.name} ${mergeRecord.mergedContact.surname || ''}? Esta acción solo puede ser realizada por administradores.`,
      okText: 'Sí, deshacer',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/merged-contacts/${mergeRecord.id}`);
          message.success('Fusión deshecha exitosamente');
          loadMergedContacts(); // Recargar la lista
          // Notificar al componente padre que el contacto ha sido actualizado
          if (onContactUpdated) {
            onContactUpdated();
          }
        } catch (error) {
          console.error('Error deshaciendo fusión:', error);
          if (error.response?.status === 403) {
            message.error('No tienes permisos para deshacer fusiones');
          } else {
            message.error('Error al deshacer la fusión');
          }
        }
      }
    });
  };

  const columns = [
    {
      title: 'Contacto Fusionado',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.mergedContact.name} {record.mergedContact.surname || ''}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.mergedContact.email}
          </div>
          {record.mergedContact.phone && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.mergedContact.phone}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Posiciones Transferidas',
      key: 'positions',
      render: (_, record) => (
        <div>
          {record.mergedContact.positions && record.mergedContact.positions.length > 0 ? (
            record.mergedContact.positions.map((position, index) => (
              <Tag key={index} style={{ marginBottom: '4px' }}>
                {position.title} en {position.entity?.name}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>Sin posiciones</span>
          )}
        </div>
      )
    },
    {
      title: 'Fusionado por',
      key: 'mergedBy',
      render: (_, record) => (
        <div>
          <div>{record.mergedByUser.name} {record.mergedByUser.surname || ''}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {formatDate(record.mergedAt)}
          </div>
        </div>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="link" 
          danger 
          size="small"
          onClick={() => handleUnfuse(record)}
        >
          Deshacer fusión
        </Button>
      )
    }
  ];

  if (mergedContacts.length === 0 && !loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end ', alignItems: 'center' }}>
           <Button 
               onClick={() => setShowMergeModal(true)}
               style={{ marginLeft: '16px' }}
             >
               Fusionar Contacto
          </Button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Divider style={{ margin: 0, flex: 1 }}>Contactos Fusionados</Divider>
          
        </div>
         
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          No hay contactos fusionados
        </div>
        
        <MergeContactModal
          visible={showMergeModal}
          onCancel={() => setShowMergeModal(false)}
          contactToMerge={contact}
          onMergeSuccess={handleMergeSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end ', alignItems: 'center' }}>
           <Button 
               onClick={() => setShowMergeModal(true)}
               style={{ marginLeft: '16px' }}
             >
               Fusionar Contacto
          </Button>
        </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Divider style={{ margin: 0, flex: 1 }}>Contactos Fusionados ({mergedContacts.length})</Divider>
      </div>
      <Table 
        dataSource={mergedContacts} 
        columns={columns} 
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 5,
          showSizeChanger: false
        }}
        size="small"
        style={{ marginTop: '16px' }}
      />
      
      <MergeContactModal
        visible={showMergeModal}
        onCancel={() => setShowMergeModal(false)}
        contactToMerge={contact}
        onMergeSuccess={handleMergeSuccess}
      />
    </>
  );
};

export default MergedContactsSection;
